import React, { PropsWithChildren } from 'react';
import SvgBreadcrumbDivider from '@icons/BreadcrumbDivider.tsx';
import COText from '@app/pages/COText/COText.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import EllipsisMiddleText from '@components/ringil3/BreadCrumb/EllipsisMiddleText.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import scrollParent from '@utils/scrollParent';

export type Location = {
  id: string;
  name: string;
}

interface ComponentProps {
  currentPosition: number;
  locationCount: number;
  idPrefix: string;
  locations?: Location[];
}

interface Props {
  position: number;
  currentPosition: number;
}

/**
 * A step of the route breadcrumb. Every step except the one being viewed navigates, and navigating
 * text uses RingilButton's link mode so it hovers exactly like every other link in the app; the
 * button's own 48px box is reset because the step sits inline in the crumb row.
 */
const Anchor: React.FC<PropsWithChildren<Props & { onClick: () => void }>> = ({ position, currentPosition, onClick, children }) => {
  if (position === currentPosition + 1) return <div>{children}</div>;

  return <RingilButton mode={'link'} onClick={onClick} label={<>{children}</>} className={'h-auto p-0'} />;
};

const NavigationBreadCrumb: React.FC<ComponentProps> = ({ currentPosition, locationCount, idPrefix, locations }) => {
  const { tr } = useTranslationLgs();

 const resolveText = (i: number) => {
   const locationName = locations?.[i - 1]?.name;
   if (locationName) {
    return <EllipsisMiddleText label={locationName} />;
   } else if (i === 1) {
     return <COText label={tr('NavigationBreadCrumb.routeStart', 'Route start')} />;
   } else if (i === locationCount) {
     return <COText label={tr('NavigationBreadCrumb.routeEnd', 'Route end')} />;
   } else {
     return <COText label={tr('NavigationBreadCrumb.locationLabelForUnNamed', 'Stop {{stop}}', { stop: i - 1})} />;
   }
 };

  // Find the nearest ancestor that actually scrolls. We deliberately skip overflow:hidden wrappers:
  // a bare element.scrollIntoView() adjusts scrollTop on every scrollable ancestor — including the
  // overflow-hidden layout wrappers — which shifts the page into an unrecoverable white gap (no scrollbar
  // to scroll back), forcing a full reload. Scoping the scroll to the real scroll container avoids that.
  const navigateTo = (position: number) => {
    const target = document.getElementById(`${idPrefix}_${position}`);
    if (!target) return;

    const container = scrollParent(target);
    if (!container) {
      target.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    // Offset of the target within the scroll content, clamped to the container's max scroll so we never
    // over-scroll past the last location into empty white space.
    const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    const maxScroll = container.scrollHeight - container.clientHeight;
    container.scrollTo({ top: Math.min(offset, maxScroll), behavior: 'smooth' });
  };

  return (
    <>
      <div id={`${idPrefix}_${currentPosition + 1}`} className={'flex flex-row items-center gap-4 flex-wrap'}>
        {Array.from({ length: locationCount }, (_, i) => i + 1).map(position => {
          return (
            <React.Fragment key={position}>
              <Anchor position={position} currentPosition={currentPosition} onClick={() => navigateTo(position)}>
                {resolveText(position)}
              </Anchor>
              {position < locationCount && <SvgBreadcrumbDivider />}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};

export default NavigationBreadCrumb;
